import styled from '@emotion/styled';
import { SelectChangeEvent, Switch } from '@mui/material';
import { GoogleMap, Libraries, Marker, useLoadScript } from '@react-google-maps/api';
import { COMMONTYPES } from 'TYPES/event.types';
import { LocationIcon } from 'assets/Icons';
import QUERY_KEYS from 'network/config/queryKeys';
import { useMutation } from 'network/mutations/useMutationHook';
import { addAddress, updateAddress } from 'network/services/address';
import { validateAddressDeliveryCallback } from 'network/services/order';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import { useMutation as rUseMutation, useQueryClient } from 'react-query';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { ADDRESS_INITIAL, contactState } from 'store/atoms/contactAtom';
import { profileAtom } from 'store/atoms/profileAtom';
import { userLocationAtom } from 'store/atoms/userLocationAtom';
import { checkFormData, validators } from 'utils/validator';
import { Modal } from '..';
import { Button, InputField } from '../../atoms';
import { CenterModalContentStyle, CenterModalStyle } from '../Modal/style';
import { AddressModalCon } from './styles';

interface Props {
  refetch?: () => void;
}

const ERR_INITIAL = { firstname: '', lastname: '', address: '', phoneNumber: '', street: '', state: '', city: '' };

// Styled components for map
const MapContainer = styled.div`
  width: 100%;
  height: 200px;
  margin-bottom: 20px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
`;

// Styled components for location button
const LocationButtonWrapper = styled.div`
  position: relative;
  margin-bottom: 16px;
`;

const LocationButton = styled.button<{ disabled: boolean; isLoading: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: ${props => (props.disabled ? '#f5f5f5' : 'linear-gradient(135deg, #0066FF 0%, #0052CC 100%)')};
  color: ${props => (props.disabled ? '#999' : 'white')};
  border: none;
  border-radius: 8px;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.3s ease;
  font-weight: 500;
  opacity: ${props => (props.disabled ? 0.6 : 1)};

  &:hover {
    background: ${props => (props.disabled ? '#f5f5f5' : 'linear-gradient(135deg, #0052CC 0%, #0047B3 100%)')};
    transform: ${props => (props.disabled ? 'none' : 'translateY(-1px)')};
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, #0066ff 0%, #0052cc 100%);
    border-radius: 8px;
    opacity: 0.1;
    z-index: -1;
    filter: blur(8px);
  }
`;

const LocationIconWrapper = styled.div<{ isLoading: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${props => (props.isLoading ? 'spin 1s linear infinite' : 'none')};

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

// Add custom styles for Google Places Autocomplete
const AutocompleteStyles = `
  .pac-container {
    z-index: 10000 !important;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    margin-top: 5px;
    font-family: inherit;
  }
  .pac-item {
    padding: 8px 10px;
    cursor: pointer;
  }
  .pac-item:hover {
    background-color: #f8f8f8;
  }
  .pac-item-selected {
    background-color: #f0f0f0;
  }
`;

// Google Maps libraries to load
const libraries: Libraries = ['places'];

export const EditAddressModal: React.FC<Props> = ({ refetch }) => {
  const profile = useRecoilValue(profileAtom);
  const [addressInfo, setAddressInfo] = useRecoilState(contactState);
  const [formData, setFormData] = useState({
    ...addressInfo,
    status: addressInfo.status !== undefined ? addressInfo.status : true // Default to true on open
  });
  const [formError, setFormError] = useState(ERR_INITIAL);
  const { street, phoneNumber, state, city } = formData;
  const queryClient = useQueryClient();
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const streetInputRef = useRef<HTMLInputElement | null>(null);
  const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const setUserLocation = useSetRecoilState(userLocationAtom);
  const locationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load Google Maps API with hardcoded API key for testing if env variable is not available
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY || '', // Fallback key for testing
    libraries
  });

  // Show error message if the API fails to load
  useEffect(() => {
    if (loadError) {
      console.error('Google Maps API failed to load:', loadError);
      enqueueSnackbar('Failed to load Google Maps. Please check your connection.', { variant: 'error' });
    }
  }, [loadError]);

  // Initialize autocomplete when the component mounts
  useEffect(() => {
    // Add custom styles for Google Places Autocomplete
    const styleEl = document.createElement('style');
    styleEl.type = 'text/css';
    styleEl.appendChild(document.createTextNode(AutocompleteStyles));
    document.head.appendChild(styleEl);

    if (isLoaded && streetInputRef.current) {
      try {
        autocompleteRef.current = new window.google.maps.places.Autocomplete(streetInputRef.current, {
          types: ['address'],
          componentRestrictions: { country: 'ng' }, // Restrict to Nigeria
          fields: ['address_components', 'formatted_address', 'geometry', 'name']
        });

        // Listen for place selection
        autocompleteRef.current?.addListener('place_changed', () => {
          const place = autocompleteRef.current?.getPlace();
          console.log('Selected place:', place); // Debug info

          if (place && place.geometry && place.geometry.location) {
            // Extract state, city, and country from address components
            let state = '';
            let city = '';
            let country = '';

            if (place.address_components) {
              for (const component of place.address_components) {
                if (component.types.includes('administrative_area_level_1')) {
                  state = component.long_name;
                }
                if (component.types.includes('locality') || component.types.includes('administrative_area_level_2')) {
                  city = component.long_name;
                }
                if (component.types.includes('country')) {
                  country = component.long_name;
                }
              }
            }

            // Update form data with selected address
            setFormData(prev => ({
              ...prev,
              street: place.formatted_address || prev.street,
              state: state || prev.state, // Auto-populate state from Google Places
              city: city || prev.city, // Also auto-populate city if available
              country: country || prev.country // Auto-populate country from Google Places
            }));

            // Update coordinates for map
            setCoordinates({
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng()
            });

            // Clear any previous validation errors
            setFormError(prev => ({
              ...prev,
              street: '',
              state: '',
              city: ''
            }));
          }
        });
      } catch (error) {
        console.error('Error initializing Google Places Autocomplete:', error);
        enqueueSnackbar('Error initializing address autocomplete', { variant: 'error' });
      }
    }

    return () => {
      // Clean up styles when component unmounts
      const existingStyle = document.querySelector('style');
      if (existingStyle && existingStyle.textContent?.includes('.pac-container')) {
        existingStyle.remove();
      }
    };
  }, [isLoaded]);

  const { mutateAsync: asyncValidateAddress } = rUseMutation(validateAddressDeliveryCallback, {
    onSuccess: data => { },
    onError: error => {
      enqueueSnackbar('Address validation failed. Please check the details and try again', { variant: 'error' });
    }
  });

  const getCurrentLocation = () => {
    if (isLoadingLocation) return;

    if (locationTimeoutRef.current) {
      clearTimeout(locationTimeoutRef.current);
    }

    if (!navigator.geolocation) {
      enqueueSnackbar('Geolocation is not supported by your browser', { variant: 'error' });
      return;
    }

    setIsLoadingLocation(true);

    navigator.geolocation.getCurrentPosition(
      async position => {
        try {
          const { latitude, longitude } = position.coords;

          // Use Google Maps Geocoding API to get address from coordinates
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${process.env.REACT_APP_GOOGLE_API_KEY}`
          );

          const data = await response.json();

          if (data.status === 'OK' && data.results.length > 0) {
            const addressResult = data.results[0];
            const formattedAddress = addressResult.formatted_address;

            // Extract components
            let state = '';
            let city = '';
            let country = '';
            let zipCode = '';

            addressResult.address_components.forEach((component: any) => {
              if (component.types.includes('administrative_area_level_1')) {
                state = component.long_name;
              } else if (component.types.includes('locality')) {
                city = component.long_name;
              } else if (component.types.includes('country')) {
                country = component.long_name;
              } else if (component.types.includes('postal_code')) {
                zipCode = component.long_name;
              }
            });

            // Update form data with location
            setFormData(prev => ({
              ...prev,
              street: formattedAddress,
              state: state || 'Lagos',
              city: city || '',
              country: country || 'Nigeria'
            }));

            // Set coordinates for map
            setCoordinates({
              lat: latitude,
              lng: longitude
            });

            // Update location state
            setUserLocation({
              lat: latitude.toString(),
              display_name: formattedAddress,
              name: formattedAddress,
              place_id: '0',
              lon: longitude.toString(),
              address: {
                state: state || 'Lagos',
                city: city || '',
                country: country || 'Nigeria',
                postcode: zipCode || ''
              }
            });

            // Clear any validation errors
            setFormError(prev => ({
              ...prev,
              street: '',
              state: '',
              city: ''
            }));

            enqueueSnackbar('Location detected successfully! Please review and confirm your address.', {
              variant: 'success'
            });
          } else {
            throw new Error(data.status || 'Could not retrieve address from coordinates');
          }
        } catch (error) {
          console.error('Error getting location:', error);
          enqueueSnackbar(`Failed to get your current location: ${(error as any).message || 'Unknown error'}`, {
            variant: 'error'
          });
        } finally {
          locationTimeoutRef.current = setTimeout(() => {
            setIsLoadingLocation(false);
          }, 1000);
        }
      },
      error => {
        console.error('Geolocation error:', error);
        let errorMessage = 'Could not get your current location';

        if (error.code === 1) {
          errorMessage = 'Location access denied. Please enable location services in your browser.';
        } else if (error.code === 2) {
          errorMessage = 'Location unavailable. Please try again.';
        } else if (error.code === 3) {
          errorMessage = 'Location request timed out. Please try again.';
        }

        enqueueSnackbar(errorMessage, { variant: 'error' });

        locationTimeoutRef.current = setTimeout(() => {
          setIsLoadingLocation(false);
        }, 1000);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const setModalClose = () => {
    setAddressInfo(ADDRESS_INITIAL);
  };

  const onInputChange = (e: COMMONTYPES['inputChange']) => {
    const { name, value } = e.target;
    const error = validators[name] ? validators[name](value) : '';
    setFormData(formData => ({ ...formData, [name]: value }));
    setFormError({
      ...formError,
      [name]: error
    });
  };

  const onDefaultChange = (e: COMMONTYPES['inputChange']) => {
    const { name, checked } = e.target;
    setFormData(formData => ({ ...formData, [name]: checked }));
  };

  const onSelectChange = (e: SelectChangeEvent<unknown>) => {
    const { name, value } = e.target;
    setFormData(formData => ({ ...formData, [name]: value }));
  };

  const { mutateAsync: newAddressMutate, isLoading: newLoading } = useMutation(addAddress, 'NEW_ADDRESS');
  const { mutateAsync: updateAddressMutate, isLoading: updateLoading } = useMutation(updateAddress, 'UPDATE_ADDRESS');

  // Check if address is complete (has state and street)
  const isAddressComplete = street.trim() !== '' && state.trim() !== '';
  const isButtonDisabled = !isAddressComplete || newLoading || updateLoading;

  const _submit = async (e: COMMONTYPES['buttonClick']) => {
    e.preventDefault();

    // Check if required form fields are valid
    const fieldsToValidate = { street, phoneNumber };

    // Add state validation
    if (!state.trim()) {
      setFormError(prev => ({ ...prev, state: 'State is required' }));
      enqueueSnackbar('Please ensure your address includes a state', { variant: 'error' });
      return;
    }

    const isValid = checkFormData(fieldsToValidate, setFormError);
    if (!isValid) return;

    const { isEditAddressModal, ...rest } = formData;
    const addrStr = `${rest.street}, ${rest.state}, ${rest.country}`;
    const name =
      profile?.customer_type === 'BUSINESS'
        ? profile?.business_name + ' Business'
        : (profile?.firstname || 'User') + ' ' + (profile?.lastname || ' User');

    // Clean phone number: keep digits only
    let rawPhone = rest.phoneNumber.replace(/[^0-9]/g, '');

    // If it starts with '0', replace it with '234'
    if (rawPhone.startsWith('0')) {
      rawPhone = '234' + rawPhone.slice(1);
    }

    // If it already starts with '234' and has 13 digits total, it's fine
    // Otherwise force it into +234 format
    if (!rawPhone.startsWith('234')) {
      enqueueSnackbar('Phone number must start with 0 or be in +234 format.', { variant: 'error' });
      return;
    }

    const formattedPhoneNumber = `+${rawPhone}`;
    // update local state but don't rely on it immediately for validation
    setFormData(f => ({ ...f, phoneNumber: formattedPhoneNumber }));

    try {
      if (formattedPhoneNumber.length !== 14) {
        enqueueSnackbar('Phone number must be in the format +2348012345678.', { variant: 'error' });
        return;
      }

      // Validate the address before adding or updating — use freshly computed phone
      const validationResult = await asyncValidateAddress({
        name,
        email: profile?.email || 'test@gmail.com',
        phone: formattedPhoneNumber,
        address: addrStr
      });

      // tolerant status check (handles many response shapes)
      const vr: any = validationResult;
      const resolvedStatus = (
        (vr?.status ?? vr?.data?.status ?? vr?.data?.data?.status ?? vr?.raw?.status) || ''
      ).toString().toLowerCase();
      const resolvedMessage =
        vr?.message || vr?.data?.message || vr?.data?.data?.message || vr?.raw?.message || 'Address validation failed. Please check the details and try again.';

      if (resolvedStatus !== 'success') {
        console.debug('Address validation response:', vr);
        enqueueSnackbar(String(resolvedMessage), { variant: 'error' });
        return;
      }

      // Proceed to add or update the address if validation succeeds
      if (rest.id) {
        await updateAddressMutate({ id: rest.id, address_code: validationResult?.data?.address_code, ...rest });
      } else {
        await newAddressMutate({
          status: rest.status,
          street: rest.street,
          state: rest.state,
          country: rest.country,
          latitude: coordinates?.lat || 0,
          longitude: coordinates?.lng || 0,
          city: rest.city,
          address_code: validationResult?.data?.address_code,
          phoneNumber: validationResult?.data?.phone
        });
      }

      // Invalidate query to refetch updated address list
      queryClient.invalidateQueries(QUERY_KEYS.CUSTOMER_ADDRESSES);
      if (refetch) refetch();

      // Reset the form data to initial state
      setAddressInfo(ADDRESS_INITIAL);
    } catch (error) {
      enqueueSnackbar('An error occurred while updating the address.', { variant: 'error' });
    }
  };

  const selectClick = (e: COMMONTYPES['divClick']) => e.stopPropagation();

  return (
    <Modal
      onModalClose={setModalClose}
      extraModalContentStyle={{ ...CenterModalContentStyle, maxWidth: '636px' }}
      extraModalStyle={CenterModalStyle}>
      <AddressModalCon>
        <h3>Address Details</h3>
        <LocationButtonWrapper>
          <LocationButton
            onClick={getCurrentLocation}
            disabled={isLoadingLocation}
            isLoading={isLoadingLocation}
            type="button">
            <LocationIconWrapper isLoading={isLoadingLocation}>
              <LocationIcon />
            </LocationIconWrapper>
            {isLoadingLocation ? 'Getting Your Location...' : 'Use My Current Location'}
          </LocationButton>
        </LocationButtonWrapper>

        <div className="name-div hidden" style={{ display: 'none' }}>
          <InputField
            label="Firstname"
            name="firstname"
            value={'firstname'}
            placeholder="Enter first name"
            required
            error={formError.firstname}
          />
          <InputField
            label="Lastname"
            name="lastname"
            value={'lastname'}
            placeholder="Enter last name"
            required
            error={formError.lastname}
          />
        </div>

        {/* Show country field as read-only display if country is auto-populated */}
        {formData.country && (
          <div>
            <label>Country (Auto-detected)</label>
            <InputField
              label=""
              name="country"
              value={formData.country}
              placeholder="Country will be auto-filled from address"
              disabled
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
            />
          </div>
        )}

        {/* Show state field as read-only display if state is auto-populated */}
        {formData.state && (
          <div>
            <label>State (Auto-detected) *</label>
            <InputField
              label=""
              name="state"
              value={formData.state}
              placeholder="State will be auto-filled from address"
              disabled
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
              error={formError.state}
            />
          </div>
        )}

        {/* Show city field if available */}
        {formData.city && (
          <div>
            <label>City (Auto-detected)</label>
            <InputField
              label=""
              name="city"
              value={formData.city}
              placeholder="City will be auto-filled from address"
              disabled
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
            />
          </div>
        )}

        <InputField
          label="Street Address"
          name="street"
          value={street}
          onChange={onInputChange}
          placeholder="Start typing your address..."
          required
          error={formError.street}
          inputRef={streetInputRef}
          autoComplete="off"
          onFocus={() => {
            setFormError(prev => ({ ...prev, street: '' }));
          }}
        />

        {/* Google Map */}
        {isLoaded && coordinates && (
          <MapContainer>
            <GoogleMap mapContainerStyle={{ width: '100%', height: '100%' }} center={coordinates} zoom={15}>
              <Marker position={coordinates} />
            </GoogleMap>
          </MapContainer>
        )}

        <InputField
          label="Phone Number"
          type="tel"
          name="phoneNumber"
          value={phoneNumber}
          onChange={onInputChange}
          placeholder="+2348012345678"
          required
          error={formError.phoneNumber}
        />

        <label
          htmlFor="save-default"
          style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <Switch id="save-default" name="status" checked={!!formData.status} onChange={onDefaultChange} />
          Save as default address
        </label>

        {/* Show warning if address is incomplete */}

        <Button
          extraStyles={{
            width: '194px',
            opacity: isButtonDisabled ? '0.6' : '1',
            cursor: isButtonDisabled ? 'not-allowed' : 'pointer'
          }}
          onClick={_submit}
          isLoading={newLoading || updateLoading}
          disabled={isButtonDisabled}>
          Confirm Address
        </Button>
      </AddressModalCon>
    </Modal>
  );
};
